/**
 * Default HERO greeting template + config type.
 *
 * Auto-fires on a MobileTrigger's first mount (session-storage gated),
 * so every dddk install shows a coherent bilingual first-visit
 * greeting without the host writing any HTML. Hosts customise three
 * axes without touching internals:
 *
 *   1. Turn it off — `heroGreeting: false`.
 *   2. Swap text — per-locale record; falls back to English if a locale
 *      is missing.
 *   3. Swap styling — every visual is a CSS variable declared in
 *      `tokens.css`. Override on `:root`.
 *
 * Full escape hatch: pass `bodyHtml` (string or `(locale) => string`)
 * to replace the whole content HTML. Everything else on the config is
 * ignored in that case.
 *
 * The default template renders:
 *   - Title line ("Hi! I'm your AI assistant here")
 *   - Two action rows:
 *       👆 Tap me → open the palette
 *       🎤 Hold me → tell me what you need
 *   - Keyboard hint (desktop only): Ctrl+K + Space
 */

/**
 * One entry in the action-row list of the default template. `icon` is
 * an emoji (or empty for no icon column); `body` is the row text —
 * either a plain string or a per-locale record.
 */
export interface HeroGreetingRow {
  icon?: string;
  body: string | Partial<Record<string, string>>;
}

/**
 * Full HERO greeting configuration surface.
 *
 * All fields are optional. Passing an empty object `{}` uses every
 * default. Passing `false` on the parent `heroGreeting` field disables
 * the auto-show entirely.
 */
export interface HeroGreetingConfig {
  /**
   * Auto-fire the greeting on the MobileTrigger's first mount when the
   * user hasn't seen it this session. Default `true`. Set `false` to
   * keep the config (for programmatic firing via
   * `mobile.showHeroGreetingWithDefaults()`) but skip the auto-fire.
   */
  autoShow?: boolean;

  /**
   * How long the greeting stays visible before auto-dismiss (ms).
   * Default `20000` (20 seconds) — long enough to read three lines of
   * guidance, short enough not to sit on top of content.
   */
  autoDismissMs?: number;

  /**
   * sessionStorage key used to mark the greeting as "seen" so it
   * doesn't fire again in the same tab session. Default
   * `'dddk_hero_greeting_seen_v1'`. Bump the suffix if you change the
   * greeting content and want every returning visitor to see it once
   * more.
   */
  storageKey?: string;

  /**
   * Delay before the greeting fires on first mount (ms). Default
   * `1200`. Gives the page a moment to render + settle so the greeting
   * doesn't land during layout thrash.
   */
  delayMs?: number;

  /**
   * Title line at the top of the greeting. Either a plain string or a
   * per-locale record. Default bilingual placeholder that reads as
   * "Hi, I'm the AI assistant here" — not brand-specific so it doesn't
   * feel out of place on any site.
   *
   *   { en: "Hi! I'm your AI assistant.", 'zh-TW': "嗨！我是這裡的 AI 助理。" }
   */
  title?: string | Partial<Record<string, string>>;

  /**
   * Action rows. Default two rows explaining the two primary
   * gestures: tap-to-open-palette, hold-to-talk. Pass your own array
   * to add / remove / reword rows.
   */
  rows?: HeroGreetingRow[];

  /**
   * Keyboard hint line at the bottom (desktop only — hidden on touch).
   * Default bilingual note about Ctrl+K + Space. Set `false` to hide
   * it even on desktop.
   */
  kbdHint?: string | Partial<Record<string, string>> | false;

  /**
   * Screen-reader label — plain-text version of the greeting for
   * assistive tech (the visible HTML has emoji + `<kbd>` markup that
   * doesn't read well). Default auto-generated from title + rows.
   */
  ariaLabel?: string | Partial<Record<string, string>>;

  /**
   * Full escape hatch — pass raw HTML (or a locale-aware function that
   * returns it) to replace the entire body. When set, every other
   * field on this config except `autoDismissMs` / `storageKey` /
   * `delayMs` / `autoShow` is ignored.
   */
  bodyHtml?: string | ((locale: string) => string);
}

/**
 * Resolve a locale-record value to a single string. Prefers the exact
 * locale, falls back to `'en'`, then the first defined value. Returns
 * `undefined` if the input is `undefined` (caller decides the
 * fallback).
 */
function pickLocale<T extends string>(
  v: T | Partial<Record<string, T>> | undefined,
  locale: string,
): T | undefined {
  if (v === undefined) return undefined;
  if (typeof v === 'string') return v;
  return v[locale] ?? v['en'] ?? Object.values(v)[0];
}

/**
 * Strip HTML tags from a string, useful for building a plain-text
 * aria-label from a snippet that includes `<b>` / `<kbd>`.
 */
function stripHtml(s: string): string {
  return s.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
}

const DEFAULT_ROWS_ZH: HeroGreetingRow[] = [
  { icon: '👆', body: { 'zh-TW': '<b>點我</b> → 打開指令面板', en: '<b>Tap</b> me → open the palette' } },
  { icon: '🎤', body: { 'zh-TW': '<b>按住我</b> → 告訴我你的問題', en: '<b>Hold</b> me → tell me what you need' } },
];

const DEFAULT_TITLE: Record<string, string> = {
  'zh-TW': '嗨！我是這裡的 AI 助理',
  en: "Hi! I'm your AI assistant here",
};

const DEFAULT_KBD_HINT: Record<string, string> = {
  'zh-TW': '鍵盤也行：<kbd>Ctrl</kbd>+<kbd>K</kbd> 開面板 · 按住 <kbd>Space</kbd> 說話',
  en: 'Or keyboard: <kbd>Ctrl</kbd>+<kbd>K</kbd> for the palette · hold <kbd>Space</kbd> to talk',
};

/**
 * Compute the bodyHtml + ariaLabel to pass into
 * `MobileTrigger.showHeroGreeting()`. Respects every override on
 * `cfg`, falls back to the built-in bilingual template.
 *
 * All the `style="..."` chunks reference CSS custom properties so
 * hosts can retune sizes / weights / colours without patching the
 * inline HTML — see `mascot.md` for the token list.
 */
export function renderHeroGreetingBody(
  cfg: HeroGreetingConfig,
  locale: string,
  ctx: { isTouch: boolean } = { isTouch: false },
): { bodyHtml: string; ariaLabel: string } {
  // Full escape hatch — host owns the whole HTML.
  if (cfg.bodyHtml !== undefined) {
    const html =
      typeof cfg.bodyHtml === 'function' ? cfg.bodyHtml(locale) : cfg.bodyHtml;
    const aria = pickLocale(cfg.ariaLabel, locale) ?? stripHtml(html) ?? 'Welcome';
    return { bodyHtml: html, ariaLabel: aria };
  }

  const title = pickLocale(cfg.title, locale) ?? pickLocale(DEFAULT_TITLE, locale)!;
  const rows = cfg.rows ?? DEFAULT_ROWS_ZH;
  const kbdHint = ctx.isTouch
    ? '' // no keyboard on touch — hint is nonsense
    : cfg.kbdHint === false
      ? ''
      : (pickLocale(cfg.kbdHint, locale)
        ?? pickLocale(DEFAULT_KBD_HINT, locale)
        ?? '');

  // Style chunks read from CSS variables declared in tokens.css. Hosts
  // override on `:root` to retune. Fallback values in `var(..., …)`
  // match what the FAB currently ships with.
  const titleStyle =
    'font-size:var(--dddk-fab-hero-title-size,17px);'
    + 'font-weight:var(--dddk-fab-hero-title-weight,700)';
  const rowStyle =
    'display:flex;align-items:center;'
    + 'gap:var(--dddk-fab-hero-row-gap,10px);'
    + 'margin-top:var(--dddk-fab-hero-row-margin,8px);'
    + 'font-size:var(--dddk-fab-hero-row-size,15px)';
  const iconStyle =
    'display:inline-flex;align-items:center;justify-content:center;'
    + 'width:var(--dddk-fab-hero-icon-size,22px);'
    + 'flex-shrink:0;font-size:16px;line-height:1';
  const kbdStyle =
    'margin-top:var(--dddk-fab-hero-kbd-margin,10px);'
    + 'font-size:var(--dddk-fab-hero-kbd-size,12.5px);'
    + 'opacity:var(--dddk-fab-hero-kbd-opacity,0.72)';

  const rowsHtml = rows
    .map((r) => {
      const body = pickLocale(r.body, locale) ?? '';
      const iconCell = r.icon
        ? `<span style="${iconStyle}">${r.icon}</span>`
        : '';
      return `<div style="${rowStyle}">${iconCell}<span>${body}</span></div>`;
    })
    .join('');

  const bodyHtml = `<div style="${titleStyle}">${title}</div>${rowsHtml}${
    kbdHint ? `<div style="${kbdStyle}">${kbdHint}</div>` : ''
  }`;

  // Auto aria-label — join title + row plain-text bodies.
  const rowsPlain = rows.map((r) => stripHtml(pickLocale(r.body, locale) ?? '')).join('. ');
  const aria =
    pickLocale(cfg.ariaLabel, locale)
    ?? `${stripHtml(title)}. ${rowsPlain}`.trim();

  return { bodyHtml, ariaLabel: aria };
}
